import { useTranslation } from 'react-i18next';
import { Badge, Button } from '../ui';
import { isActionable, selectionRef } from './selection';
import PanelState from './PanelState';

/**
 * The optional right-hand detail panel (spec §8.2 "Quellen- und Detailpanel
 * nur bei Bedarf"). On desktop it is a column, on tablet and mobile the
 * explorer hands the same content to a `<Drawer/>`.
 *
 * @param {object} props
 * @param {import('./selection').KnowledgeSelection|null} props.selection
 * @param {() => void} props.onExplain
 * @param {() => void} props.onEtch
 * @param {(target: string) => void} [props.onImpactAnalysis]
 * @param {string} props.headingId
 */
export function DetailPanel({ selection, onExplain, onEtch, onImpactAnalysis, headingId }) {
  const { t } = useTranslation();

  if (!isActionable(selection)) {
    return (
      <div className="etch-knowledge__detail-body">
        <h2 id={headingId} className="etch-visually-hidden">
          {t('knowledge.details')}
        </h2>
        <PanelState status="empty" title={t('knowledge.noSelection')} compact />
      </div>
    );
  }

  const data = selection.data || {};
  const rows = [
    [t('knowledge.column.name'), selection.name],
    [t('knowledge.column.kind'), selection.symbolKind || t(`knowledge.kind.${selection.kind}`, { defaultValue: selection.kind })],
    [t('knowledge.column.path'), selection.path || selection.file],
    [t('knowledge.column.source'), selectionRef(selection)],
    [t('knowledge.column.layer'), data.boundary?.layer || data.role],
    [t('knowledge.column.fanIn'), data.coupling?.fanIn],
    [t('knowledge.column.fanOut'), data.coupling?.fanOut],
    [t('knowledge.column.loc'), data.metrics?.loc ?? data.loc],
    [t('knowledge.column.authority'), selection.authority],
    [t('knowledge.column.status'), selection.status],
    [t('knowledge.column.chunks'), selection.chunkCount]
  ].filter(([, value]) => value !== undefined && value !== null && value !== '');

  const impactTarget = selection.path || selection.file || selection.name;
  const canAnalyse = Boolean(onImpactAnalysis && impactTarget && selection.kind !== 'document');

  return (
    <div className="etch-knowledge__detail-body">
      <h2 id={headingId} className="etch-knowledge__detail-title">
        {selection.name}
      </h2>
      {data.coupling?.inCycle && (
        <Badge tone="warning">{t('knowledge.inCycle')}</Badge>
      )}

      <dl className="etch-knowledge__meta">
        {rows.map(([label, value]) => (
          <div className="etch-knowledge__meta-row" key={label}>
            <dt className="etch-knowledge__meta-key">{label}</dt>
            <dd className="etch-knowledge__meta-val">{String(value)}</dd>
          </div>
        ))}
      </dl>

      <div className="etch-knowledge__detail-actions">
        <Button variant="secondary" size="compact" onClick={onExplain}>
          {t('knowledge.explain')}
        </Button>
        <Button size="compact" onClick={onEtch}>
          {t('knowledge.etchIt')}
        </Button>
        {canAnalyse && (
          <Button variant="ghost" size="compact" onClick={() => onImpactAnalysis(impactTarget)}>
            {t('knowledge.impact')}
          </Button>
        )}
      </div>
    </div>
  );
}

export default DetailPanel;
